import React from "react";
import { Link } from "react-router-dom";
import { Star, Waves, MapPin, ExternalLink, Check, Car } from "lucide-react";
import FacilityIcon from "./FacilityIcon";
import OperatorCover from "./OperatorCover";
import { useCompare } from "@/lib/CompareContext";
import { formatGBP } from "@/lib/api";
import { formatKm, formatDriveTime } from "@/lib/geo";

export default function ParkCard({ park }) {
  const { toggle, has, items } = useCompare();
  const isCompared = has(park.slug);
  const compareDisabled = !isCompared && items.length >= 3;

  return (
    <article className="park-card group" data-testid={`park-card-${park.slug}`}>
      <Link to={`/park/${park.slug}`} className="block relative">
        <div className="aspect-[16/10] overflow-hidden bg-sand">
          <OperatorCover park={park} />
        </div>
        {park.featured && (
          <span className="absolute top-3 left-3 bg-terracotta text-white text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full" data-testid="featured-badge">
            Featured
          </span>
        )}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-full px-2 py-1 text-xs font-semibold flex items-center gap-1">
          <Star className="w-3 h-3 fill-terracotta text-terracotta" />
          {park.rating}
          <span className="text-ink/50">({park.review_count.toLocaleString()})</span>
        </div>
      </Link>

      <div className="p-4 flex flex-col gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs">
            <span className="label-eyebrow">{park.operator}</span>
            <span className="text-ink/30">·</span>
            <span className="text-ink/60 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{park.region}</span>
          </div>
          <Link to={`/park/${park.slug}`} className="block mt-1">
            <h3 className="font-display font-semibold text-lg leading-snug text-ink group-hover:text-forest transition-colors">
              {park.name}
            </h3>
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-ink/70">
          {park.facilities.slice(0, 6).map((f) => (
            <FacilityIcon key={f} name={f} />
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-ink/60 border-t border-dashed border-border pt-3">
          <span className="inline-flex items-center gap-1"><Waves className="w-3.5 h-3.5 text-sky" />{park.beach_distance_km < 1 ? `${Math.round(park.beach_distance_km*1000)}m` : `${park.beach_distance_km}km`} to beach</span>
          {park.pet_friendly && <span className="chip">Pet friendly</span>}
          {park.adults_only && <span className="chip">Adults only</span>}
        </div>

        {park.distance_km != null && (
          <div className="flex items-center gap-3 text-xs bg-sand rounded-lg px-3 py-2" data-testid={`distance-${park.slug}`}>
            <span className="inline-flex items-center gap-1 font-semibold text-ink"><MapPin className="w-3.5 h-3.5 text-forest" />{formatKm(park.distance_km)} away</span>
            <span className="text-ink/30">·</span>
            <span className="inline-flex items-center gap-1 text-ink/70"><Car className="w-3.5 h-3.5 text-forest" />{formatDriveTime(park.drive_minutes)}</span>
          </div>
        )}

        <div className="flex items-end justify-between mt-1">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink/50">indicative from</div>
            <div className="font-display text-2xl font-bold text-forest leading-none">
              {formatGBP(park.pricing.per_night)}<span className="text-sm font-medium text-ink/60"> /night</span>
            </div>
            <div className="text-xs text-ink/50 mt-0.5">{formatGBP(park.pricing.total)} total · {park.pricing.nights} night{park.pricing.nights>1?"s":""}</div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => toggle(park)}
              disabled={compareDisabled}
              className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
                isCompared ? "bg-forest text-white border-forest" : "bg-white border-border hover:border-forest"
              } ${compareDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`compare-toggle-${park.slug}`}
            >
              {isCompared ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" />Comparing</span> : "+ Compare"}
            </button>
            <a
              href={park.booking_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="btn-primary text-xs px-4 py-2"
              data-testid={`view-deal-${park.slug}`}
              title="Opens the operator's site so you can confirm dates, availability and the actual price"
            >
              Check availability <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
